import { SuspenseSelect } from '@/components/Suspense';
import {
  BaseOption,
  useModelsToOptionsWithData,
} from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import {
  FormSelect,
  FormSelectProps,
  SelectProps,
} from '@moneyforward/ap-frontend-components';
import { useGetTargetApplicantsForInvoiceReports } from '@service/invoice_reports/TargetApplicants.service';
import { OfficeMember as OfficeMemberModel } from 'ap-openapi';
import {
  KeyboardEvent,
  memo,
  startTransition,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { FieldValues } from 'react-hook-form';

export type Props<TFieldValues extends FieldValues> = {
  selectProps?: Omit<
    FormSelectProps<TFieldValues>['selectProps'],
    'options' | 'mode'
  >;
  query?: string;
  per?: number;
  display_all_roles?: boolean;
  include_agent_applicant?: boolean;
  defaultOptions?: BaseOption[];
} & Omit<FormSelectProps<TFieldValues>, 'selectProps'>;

export const _AgentApplicantTargetOfficeMember = <
  TFieldValues extends FieldValues
>({
  selectProps: _selectProps,
  defaultOptions: _defaultOptions,
  rules,
  per = 25,
  display_all_roles,
  include_agent_applicant,
  ...rest
}: Props<TFieldValues>) => {
  const defaultOptions = useMemo(
    () => _defaultOptions ?? [],
    [_defaultOptions]
  );
  const { t } = useTranslation();
  const [query, setQuery] = useState<string | undefined>(undefined);
  const {
    data: apiData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetTargetApplicantsForInvoiceReports({
    query,
    per,
    display_all_roles,
    include_agent_applicant,
  });

  const officeMembers = useMemo(
    () =>
      apiData?.pages.flatMap((page) => page.data.office_members ?? []) ?? [],
    [apiData]
  );
  const options = useModelsToOptionsWithData<OfficeMemberModel>(
    officeMembers,
    query,
    defaultOptions,
    { labelKeys: ['name'], caption: 'number' }
  );

  const fetchMore = useCallback(
    async (callback: () => void) => {
      if (hasNextPage) await fetchNextPage();
      callback();
    },
    [fetchNextPage, hasNextPage]
  );

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const onSearch = useCallback((value: string) => {
    startTransition(() => {
      setQuery(value);
    });
  }, []);

  const selectProps: SelectProps = useMemo(() => {
    return {
      placeholder: t('office_member_placeholder'),
      mode: 'multiple',
      ..._selectProps,
      options,
      fetchMore,
      comboBox: true,
      onSearch,
      loading: isFetchingNextPage,
      inputPlaceholder: t('office_member_input_placeholder'),
      onSearchDebounceWait: 600,
      onKeyPress: handleKeyPress,
    };
  }, [t, _selectProps, options, fetchMore, onSearch, isFetchingNextPage]);

  return (
    <FormSelect<TFieldValues>
      selectProps={selectProps}
      {...rest}
      rules={rules}
    />
  );
};
_AgentApplicantTargetOfficeMember.displayName =
  '_AgentApplicantTargetOfficeMember';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const AgentApplicantTargetOfficeMember = memo(
  (props: Props<FieldValues>) => (
    <SuspenseSelect>
      <_AgentApplicantTargetOfficeMember {...props} />
    </SuspenseSelect>
  )
) as typeof _AgentApplicantTargetOfficeMember;
AgentApplicantTargetOfficeMember.displayName =
  'AgentApplicantTargetOfficeMember';
